// Training Bundle IDs (tier_level: 'none' - require separate enrollment)
export const TRAINING_BUNDLE_IDS = {
  FACHKRAFT_MUSIK: 'ca8eceb2-37a2-4e5d-9e2d-cecace260559',
  FACHKRAFT_GITARRE: 'dda47981-25bf-478e-99fa-25b113d49a1b',
  FACHKRAFT_MUSIK_SPRACHE: '54c84c3f-ec41-4a4c-9b0c-4f5e9cdee3fb',
} as const;

// External landing pages for training programs
export const TRAINING_BUNDLE_LANDING_PAGES: Record<string, string> = {
  [TRAINING_BUNDLE_IDS.FACHKRAFT_MUSIK]: 
    'https://www.musikonzept.de/ausbildung/fachkraft-musik',
  [TRAINING_BUNDLE_IDS.FACHKRAFT_GITARRE]: 
    'https://www.musikonzept.de/ausbildung/fachkraft-gitarre',
  [TRAINING_BUNDLE_IDS.FACHKRAFT_MUSIK_SPRACHE]:
    'https://www.musikonzept.de/ausbildung/fachkraft-musik-und-sprache',
};

// All training bundle IDs as an array for easy checking
export const ALL_TRAINING_BUNDLE_IDS = Object.values(TRAINING_BUNDLE_IDS);

/**
 * Offene Austauschgruppe (Daniel, 14.10.2026): ein Treffen im Monat für beide Ausbildungen,
 * Fachkraft für Musik und Fachkraft für Musik und Sprache. Eigener Termin-Typ, damit Musik und
 * Sprache nur die Austauschgruppe sieht und nicht die übrigen Webinare von Fachkraft für Musik.
 * Dieselbe Zuordnung steht in `ausbildungs_webinar_fuer_mich()`, `labs_ausbildungs_webinare_buchen()`
 * (Migration `20261014200000`), in `create-termin-checkout` und in `austauschgruppe-einladung/logik.ts`;
 * `austauschgruppe.test.ts` hält alle gleich.
 */
export const AUSTAUSCHGRUPPE_TYP = "Ausbildungs-Webinare (Austauschgruppe)";
export const AUSTAUSCHGRUPPE_BUNDLES: readonly string[] = [
  TRAINING_BUNDLE_IDS.FACHKRAFT_MUSIK,
  TRAINING_BUNDLE_IDS.FACHKRAFT_MUSIK_SPRACHE,
];

/**
 * Anzeige einer Ausbildung im Admin: Kurzname fürs Abzeichen in der Tabelle,
 * Langname für Kennzahlenkarte und Filter, Schlüssel für den
 * `?bundle=`-Parameter.
 *
 * Bewusst eine Liste statt Verzweigungen im UI. Die Teilnehmerübersicht hat
 * bis zum 28.09.2026 an fünf Stellen „wenn Fachkraft Musik, sonst Gitarre"
 * geprüft. Dadurch wurde jede:r Teilnehmer:in der Ausbildung „Musik und
 * Sprache" als Gitarre ausgewiesen, und im Filter fehlte sie ganz, obwohl die
 * Daten längst geladen wurden. Wer eine Ausbildung hinzufügt, ergänzt hier
 * eine Zeile, und alle fünf Stellen ziehen mit.
 */
export interface TrainingBundleAnzeige {
  bundleId: string;
  /** Abzeichen in der Tabelle, muss kurz sein. */
  kurzname: string;
  /** Kennzahlenkarte und Filtereintrag. */
  langname: string;
  /** Wert für `?bundle=` in der URL, damit Sidebar-Links filtern können. */
  urlKey: string;
  /** Klassen fürs Abzeichen. Nur definierte Tokens, siehe Hinweis unten. */
  badgeClass: string;
  /** Klasse fürs Symbol auf der Kennzahlenkarte. */
  iconClass: string;
  /**
   * Kann es bei dieser Ausbildung Teilnehmende von der alten Homepage geben?
   *
   * `profiles.legacy_homepage_training` hängt am NUTZER, nicht an der
   * Einschreibung. Wer die alte Fachkraft Musik über die alte Homepage
   * gemacht hat, trägt das Kennzeichen dauerhaft. Ohne diese Angabe stand
   * „Alte Homepage" deshalb auch bei einer Ausbildung, die es damals noch
   * gar nicht gab.
   */
  kannLegacyHaben: boolean;
  /**
   * Offizieller Start, falls die Ausbildung erst später aufgeht (ISO).
   *
   * Vorher gibt es keinen sinnvollen Countdown und keinen Fortschritt: Die
   * Kurse sind noch nicht da. `undefined` heißt „läuft von Anfang an".
   */
  startetAm?: string;
  /**
   * Zugang ohne Laufzeit: Wer einmal gekauft hat, behält ihn. Dann gibt es in
   * der Admin-Ansicht keinen Countdown und kein „Abgelaufen".
   *
   * Muss mit `BUNDLES_OHNE_ABLAUF` in
   * `supabase/functions/_shared/dauerhafter-zugang.ts` übereinstimmen, dort
   * entscheidet die Kasse, ob `expires_at` gesetzt wird. Ein Test wacht darüber.
   */
  unbefristet?: boolean;
}

/**
 * Reihenfolge = Reihenfolge im Filter und bei den Kennzahlenkarten.
 *
 * > Die Farben benutzen absichtlich nur `primary`, `accent` und `foreground`.
 * > Vorher stand hier `border-warning` und `text-warning-foreground`, und
 * > diese Klassen gibt es in diesem Projekt nicht: weder in `index.css` noch
 * > in `tailwind.config.ts`. Sie waren wirkungslos.
 */
export const TRAINING_BUNDLE_ANZEIGE: readonly TrainingBundleAnzeige[] = [
  {
    bundleId: TRAINING_BUNDLE_IDS.FACHKRAFT_MUSIK,
    kurzname: "Musik",
    langname: "Fachkraft Musik",
    urlKey: "musik",
    badgeClass: "border-primary text-primary",
    iconClass: "text-primary",
    kannLegacyHaben: true,
  },
  {
    bundleId: TRAINING_BUNDLE_IDS.FACHKRAFT_GITARRE,
    kurzname: "Gitarre",
    langname: "Fachkraft Gitarre",
    urlKey: "gitarre",
    badgeClass: "border-accent text-accent",
    iconClass: "text-accent",
    kannLegacyHaben: true,
  },
  {
    bundleId: TRAINING_BUNDLE_IDS.FACHKRAFT_MUSIK_SPRACHE,
    kurzname: "Musik & Sprache",
    langname: "Fachkraft Musik und Sprache",
    urlKey: "musik-und-sprache",
    badgeClass: "border-foreground/40 text-foreground",
    iconClass: "text-foreground/70",
    // Erstmals am 26.09.2026 verkauft, es gab sie auf der alten Homepage nie.
    kannLegacyHaben: false,
    // Muss mit AUSBILDUNG_MS_FREISCHALTUNG_ISO übereinstimmen, ein Test wacht
    // darüber. Bewusst hier nochmal und nicht importiert: ausbildungFreischaltung.ts
    // ist als befristete Datei angelegt und verschwindet nach dem Start wieder.
    startetAm: "2026-10-15T00:00:00+02:00",
    // Daniel, 28.09.2026: „Die haben immer Zugriff, wenn man es einmal
    // gekauft hat."
    unbefristet: true,
  },
] as const;

/** Anzeige zu einer Bundle-ID, oder `null` wenn es keine Ausbildung ist. */
export function trainingAnzeige(
  bundleId: string | null | undefined,
): TrainingBundleAnzeige | null {
  if (!bundleId) return null;
  return TRAINING_BUNDLE_ANZEIGE.find((a) => a.bundleId === bundleId) ?? null;
}

/** Bundle-ID zum `?bundle=`-Schlüssel, oder `"all"`. */
export function bundleIdAusUrlKey(urlKey: string | null | undefined): string {
  if (!urlKey) return "all";
  return TRAINING_BUNDLE_ANZEIGE.find((a) => a.urlKey === urlKey)?.bundleId ?? "all";
}

/** `?bundle=`-Schlüssel zu einer Bundle-ID, oder `null` für „alle". */
export function urlKeyAusBundleId(bundleId: string): string | null {
  return TRAINING_BUNDLE_ANZEIGE.find((a) => a.bundleId === bundleId)?.urlKey ?? null;
}

/**
 * Darf bei dieser Ausbildung das Kennzeichen „Alte Homepage" erscheinen?
 *
 * Unbekannte Bundles gelten als alt genug: Lieber einen Hinweis zu viel als
 * einen verschwiegenen bei den Bestandsausbildungen.
 */
export function trainingKannLegacyHaben(bundleId: string | null | undefined): boolean {
  return trainingAnzeige(bundleId)?.kannLegacyHaben ?? true;
}

/** Hat diese Ausbildung einen Zugang ohne Laufzeit? */
export function trainingUnbefristet(bundleId: string | null | undefined): boolean {
  return trainingAnzeige(bundleId)?.unbefristet === true;
}

/** Startdatum der Ausbildung, falls sie erst später aufgeht. */
export function trainingStartetAm(bundleId: string | null | undefined): Date | null {
  const iso = trainingAnzeige(bundleId)?.startetAm;
  return iso ? new Date(iso) : null;
}

// Helper: Get training bundle ID for a course (if any)
export function getTrainingBundleForCourse(
  courseId: string, 
  bundleCourses: { bundleId: string; courseId: string }[]
): string | null {
  const match = bundleCourses.find(
    bc => bc.courseId === courseId && ALL_TRAINING_BUNDLE_IDS.includes(bc.bundleId as any)
  );
  return match?.bundleId || null;
}

// Helper: Check if a course belongs to any training bundle
export function isTrainingCourse(
  courseId: string,
  bundleCourses: { bundleId: string; courseId: string }[]
): boolean {
  return getTrainingBundleForCourse(courseId, bundleCourses) !== null;
}
